import type {
  IPresentationCreatorThreeDRegistry,
  IThreeDPresentationFactory,
  IPresentationFactoryBag,
  IObjectViewModel,
  IMeasurementViewModel,
  IViewPortController,
  IRaycasterProvider,
  ICenterLineViewModel
} from '@/engine/app/interfaces.ts';
import type {
  IPickableInfo,
  IPresentationChangeHandler,
  IUiThreeDPresentationContext,
  IUiTwoDPresentationContext
} from '@/engine/UIPresentation/Interfaces.ts';
import type { PlanningAlgorithm } from '../PlanningAlgorithm/PlanningAlgorithm';
import type { ViewsService } from './Views.service';
import type { ViewController } from './View.controller';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service';
import type { ICommandProcessor } from './modules/core/interfaces/Command';
import type { IRemoveCommandsFactory } from './modules/indications/IndicationRemoveHighlighter';
import type { ImagesService } from './modules/images/Images.service';
import { Drawer } from '../UIPresentation/Drawer';
import { CenterlinePresentation } from '../UIPresentation/CenterlinePresentation';
import { PolyLineHighlighter } from '../UIPresentation/highlighters/PolyLineHighlighter';

export class ThreeDPresentationFactory implements IThreeDPresentationFactory {
  private _registry: IPresentationCreatorThreeDRegistry;

  private _bag: IPresentationFactoryBag;

  public constructor(
    registry: IPresentationCreatorThreeDRegistry,
    planningAlgorithm: PlanningAlgorithm,
    viewsService: ViewsService,
    viewController: ViewController,
    loggerService: ObservabilityLoggerService,
    commandProcessor: ICommandProcessor,
    removeIndicationCommandsFactory: IRemoveCommandsFactory,
    viewportController: IViewPortController,
    imagesService: ImagesService,
    viewPortDescriptor: string
  ) {
    this._registry = registry;
    this._bag = {
      planningAlgorithm: planningAlgorithm,
      viewsService: viewsService,
      viewController: viewController,
      drawer: new Drawer(),
      loggerService: loggerService,
      commandProcessor: commandProcessor,
      removeIndicationCommandsFactory: removeIndicationCommandsFactory,
      viewportController: viewportController,
      imagesService: imagesService,
      viewPortDescriptor: viewPortDescriptor
    };
  }

  public setRaycasterProvider(raycasterProvider?: IRaycasterProvider): void {
    this._bag.raycasterProvider = raycasterProvider;
  }

  public createThreeDObjectPresentation(
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._registry
      .resolveThreeDObjectCreator(viewModel.kind)
      ?.createPresentation(this._bag, viewModel, changeHandler);
  }

  public createThreeDMeasurementPresentation(
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._registry
      .resolveThreeDMeasurementCreator(viewModel.kind)
      ?.createPresentation(this._bag, viewModel, changeHandler);
  }

  public createTwoDObjectPresentation(viewModel: IObjectViewModel): IUiTwoDPresentationContext | undefined {
    return this._registry.resolveTwoDObjectCreator(viewModel.kind)?.createPresentation(this._bag, viewModel);
  }

  public createTwoDMeasurementPresentation(viewModel: IMeasurementViewModel): IUiTwoDPresentationContext | undefined {
    return this._registry.resolveTwoDMeasurementCreator(viewModel.kind)?.createPresentation(this._bag, viewModel);
  }

  public createTwoDMeasurementLabelPresentation(
    viewModel: IMeasurementViewModel
  ): IUiTwoDPresentationContext | undefined {
    return this._registry.resolveTwoDMeasurementLabelCreator(viewModel.kind)?.createPresentation(this._bag, viewModel);
  }

  public createCenterLinePresentation(
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext {
    const clPresentation = new CenterlinePresentation(
      this._bag.planningAlgorithm,
      viewModel,
      changeHandler,
      this._bag.viewportController,
      this._bag.viewportController.getCameraController(),
      this._bag.imagesService
    );

    const pickables = new Array<IPickableInfo>();
    pickables.push({
      pickable: new PolyLineHighlighter(
        viewModel,
        clPresentation,
        this._bag.imagesService,
        this._bag.viewsService,
        this._bag.viewController,
        this._bag.planningAlgorithm,
        this._bag.commandProcessor
      )
    });

    return {
      presentation: clPresentation,
      raycastable: clPresentation,
      pickables: pickables
    };
  }
}
